



















































































import { defineComponent, ref, reactive, computed } from "@vue/composition-api";

import Layout from "@/components/Atoms/Layout/index.vue";
import HelpLink from "@/components/Atoms/HelpLink/index.vue";
import OccButton from "@/components/Atoms/OccButton/index.vue";
import LoadingView from "@/components/Molecules/LoadingView/index.vue";
import PageHeader from "@/components/Molecules/PageHeader/index.vue";
import TableFooter from "@/components/Molecules/TableFooter/index.vue";
import RoundtableUserList from "@/components/Organisms/RoundtableUserList/index.vue";
import RoundtableFilter from "@/components/Organisms/RoundtableFilter/index.vue";
import type { Props } from "@/components/Molecules/ListTable/index.vue";
import type { RoundtableAttendeesFilterData } from "@/components/Organisms/RoundtableFilter/type";
import CsvCustomizeModal from "@/components/Organisms/CsvCustomizeModal/index.vue";
import ConfirmModal from "@/components/Organisms/modals/ConfirmModal/index.vue";
import { CsvColumns } from "@/components/Organisms/CsvCustomizeModal/types";
import OccAlertFloating from "@/components/Molecules/OccAlert/Floating/index.vue";

import {
  getRoundtableAttendees,
  getRoundtableAttendeeSearchOptions,
  getPartnerEntryCsvColumns,
  upsertPartnerEntryCsvColumns,
  startGenerateRoundtableAtendeesCsv
} from "@/api";
import type { ApiRequest, ApiResponse, Order } from "@/api/types";
import { useRoute } from "@/router";
import { TableHeader } from "@/constants/roundtable"
import { useRequest } from "@/helpers/hooks/useRequest";
import {
  PRIVILEGE_ENTRIES_DOWNLOAD,
  hasPrivilege
} from "@/helpers/privilege-helper";
import { AvailableCsvColumns } from "@/helpers/sponsoredEventEntryApplicants";

export default defineComponent({
  components: {
    Layout,
    HelpLink,
    OccButton,
    LoadingView,
    PageHeader,
    TableFooter,
    RoundtableUserList,
    RoundtableFilter,
    CsvCustomizeModal,
    ConfirmModal,
    OccAlertFloating
  },
  name: "onlineRountableUsers",
  setup() {
    const route = useRoute();
    const [{ data: searchOptions, loading: searchOptionsLoading }] = useRequest(
      () => getRoundtableAttendeeSearchOptions({ format: "online" }),
      {
        immediate: true
      }
    );

    const [
      { data: roundtableAttendees, loading: roundtableLoading },
      runGetRoundtableAttendees
    ] = useRequest(
      () =>
        getRoundtableAttendees({
          ...searchParams.value,
          limit: displayCount.value,
          offset: displayCount.value * (currentPage.value - 1)
        }),
      {
        immediate: true
      }
    );

    const filterState = reactive<RoundtableAttendeesFilterData>({
      keyword: "",
      universities: [] as string[],
      graduateYears: [] as string[],
      grades: [] as string[],
      schedules: [] as number[],
      event_ids: [] as number[],
      entryDate: { from: "", to: "" }
    });

    if (route.query.eventId) {
      filterState.event_ids = [Number(route.query.eventId)];
    }

    const searchParams = computed<ApiRequest.GetRoundtableAttendees>(() => {
      const eventIds = filterState.event_ids.map(id => String(id));
      const params: ApiRequest.GetRoundtableAttendees = {
        format: "online",
        keyword: filterState.keyword,
        universities: filterState.universities,
        graduate_years: filterState.graduateYears,
        grades: filterState.grades,
        event_ids: eventIds,
        schedule_ids: filterState.schedules,
        entry_date_from: filterState.entryDate.from,
        entry_date_to: filterState.entryDate.to,
        order: sortState.order
      };

      return params;
    });

    const isLoading = computed<boolean>(() => {
      return !!roundtableLoading.value || !!searchOptionsLoading.value;
    });

    const tableHeader = reactive(TableHeader as Props<{ sortParam: "entried_at" }>["headers"]);

    const hasCsvDownloadPriviledge = hasPrivilege(PRIVILEGE_ENTRIES_DOWNLOAD);

    /** ========================================
     * フィルタ
     * ========================================*/
    const onSubmitFilter = async () => {
      moveFiresPage();
      await runGetRoundtableAttendees();
    };

    /** ========================================
     * ページネーション
     * ========================================*/
    const displayCountSelectList = [
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" }
    ];

    const currentPage = ref(1);
    const displayCount = ref(25);

    const totalPageCount = computed(() => {
      if (!roundtableAttendees.value || !roundtableAttendees.value.total) {
        return 1;
      }
      return Math.ceil(roundtableAttendees.value?.total / displayCount.value);
    });

    const moveFiresPage = () => {
      currentPage.value = 1;
    };

    const onChangePage = async (pageNumber: number) => {
      currentPage.value = pageNumber;
      await runGetRoundtableAttendees();
    };

    const onChangeDisplayCount = async (count: number) => {
      displayCount.value = count;
      moveFiresPage();
      await runGetRoundtableAttendees();
    };

    /** ========================================
     * ソート
     * ========================================*/
    const sortState = reactive<
      Pick<ApiRequest.GetRoundtableAttendees, "order">
    >({
      order: "desc"
    });

    const onChangeOrder = async (
      order: Order,
      headerIndex: number,
    ) => {
      const targetHeader = tableHeader[headerIndex];
      const sort = targetHeader.sortParam;

      if (!sort) {
        return;
      }

      sortState.order = order;
      targetHeader.order = order;
      moveFiresPage();

      await runGetRoundtableAttendees();
    };

    /** ========================================
     * モーダル
     * ========================================*/
    const csvCustomizeModalShow = ref<boolean>(false);

    const closeCsvCustomizeModal = () => {
      csvCustomizeModalShow.value = false;
    };

    const showCsvCustomizeModal = async () => {
      await runGetPartnerEntryCsvColumns();
      csvCustomizeModalShow.value = true;
    };

    /** ========================================
     * CSV出力カラムの管理
     * ========================================*/
    const partnerEntryCsvColumns = ref<ApiResponse.PartnerEntryCsvColumn[]>();
    const excludedColumns = ref<CsvColumns[]>([]);
    const includedColumns = ref<CsvColumns[]>([]);

    const runGetPartnerEntryCsvColumns = async () => {
      const response = await getPartnerEntryCsvColumns();
      partnerEntryCsvColumns.value = response.data.csv_columns;
      updateDisplayCsvColumns();
    };

    const updateDisplayCsvColumns = () => {
      if (!partnerEntryCsvColumns.value) {
        return;
      }

      includedColumns.value = [];
      excludedColumns.value = [];

      partnerEntryCsvColumns.value.forEach(item => {
        const csvColumnObj = AvailableCsvColumns.find(
          column => column.name === item.column
        );
        if (csvColumnObj) {
          includedColumns.value.push(csvColumnObj);
        }
      });

      excludedColumns.value = AvailableCsvColumns.filter(item => {
        return !includedColumns.value.find(includedCol => {
          return includedCol.name === item.name;
        });
      });
    };

    const csvColumnsSaveParams = computed(() => {
      const csvColumns = includedColumns.value.map((item, idx) => {
        return { column: item.name, position: idx };
      });
      // NOTE: partner_entry_offerの出力カラムはOCCでは出力可能カラムに含まれない。
      //       旧管理画面では設定可能なため、併用している時誤って削除されないよう、登録されていれば末尾に追加する。
      //       なお、OCCにおけるCSV出力時にはこの設定は無視される。
      if (
        partnerEntryCsvColumns.value?.find(
          item => item.column === "partner_entry_offer"
        )
      ) {
        const insertIndex = csvColumns.length;
        csvColumns.push({
          column: "partner_entry_offer",
          position: insertIndex
        });
      }
      // NOTE: resume_answersの出力カラムは応募者一覧では出力可能カラムに含まれない。
      //       イベント申込者一覧では設定可能なため、保存時に削除はせず、登録されていれば末尾に追加する。
      //       なお、応募者一覧におけるCSV出力時にはこの設定は無視される。
      if (
        partnerEntryCsvColumns.value?.find(
          item => item.column === "entry_route"
        )
      ) {
        const insertIndex = csvColumns.length;
        csvColumns.push({
          column: "entry_route",
          position: insertIndex
        });
      }

      return {
        csv_columns: csvColumns
      };
    });

    const runUpsertPartnerEntryCsvColumns = async () => {
      const response = await upsertPartnerEntryCsvColumns(
        csvColumnsSaveParams.value
      );
      if (response.status !== 204) {
        showCsvDownloadError();
      }
    };

    const updateCsvIncludedColumns = (
      newArray: { name: string; label: string }[]
    ) => {
      includedColumns.value = newArray;
    };

    const updateCsvExcludedColumns = (
      newArray: { name: string; label: string }[]
    ) => {
      excludedColumns.value = newArray;
    };

    /** ========================================
     * CSVダウンロード
     * ========================================*/
    const csvDownloadColumns = ref<string[]>();
    const csvDownloadConfirmModalShow = ref(false);

    const csvDownloadParams = computed<ApiRequest.RoundtableAtendeesGenerateCsv>(() => {
      return {
        ...searchParams.value,
        event_ids: searchParams.value.event_ids
          ?.map(id => parseInt(id))
          .filter(id => !Number.isNaN(id)),
        output_columns: csvDownloadColumns.value || [],
        format: "online"
      };
    });

    const onClickCsvDownload = () => {
      showCsvCustomizeModal();
    };

    const applyCsvDownload = async (items: string[]) => {
      await Promise.all([
        runUpsertPartnerEntryCsvColumns(),
        runCsvDownload(items)
      ]);
    };

    const runCsvDownload = async (items: string[]) => {
      csvDownloadColumns.value = items;
      const response = await startGenerateRoundtableAtendeesCsv(
        csvDownloadParams.value
      );
      if (response.status === 200) {
        csvDownloadConfirmModalShow.value = true;
      } else {
        showCsvDownloadError();
      }
    };

    const showCsvDownloadError = () => {
      alertTextList.value = [
        "ダウンロードに失敗しました。リロードして再度お試しください。"
      ];
      showAlert();
    };

    const closeCsvDownloadConfirmModal = () => {
      csvDownloadConfirmModalShow.value = false;
    };

    /** ========================================
     * アラート
     * ========================================*/
    const alertVisible = ref<Boolean>(false);

    const alertTextList = ref<string[]>([]);

    const showAlert = () => {
      alertVisible.value = true;
    };

    return {
      isLoading,
      tableHeader,
      roundtableAttendees,
      searchOptions,
      filterState,

      onSubmitFilter,

      hasCsvDownloadPriviledge,

      displayCountSelectList,
      currentPage,
      displayCount,
      totalPageCount,
      onChangePage,
      onChangeDisplayCount,

      onChangeOrder,

      onClickCsvDownload,
      csvCustomizeModalShow,
      closeCsvCustomizeModal,
      showCsvCustomizeModal,
      csvDownloadConfirmModalShow,
      closeCsvDownloadConfirmModal,
      excludedColumns,
      includedColumns,
      applyCsvDownload,
      updateCsvIncludedColumns,
      updateCsvExcludedColumns,

      alertVisible,
      alertTextList
    };
  }
});
